import type { DateTime } from "luxon";
import type { Department } from "../../../contexts/AuthProvider";
import { byDimensionsOf } from "../../../utils/censusResolution";
import { demoRequiredForDay } from "../../../utils/demoRequiredStaff";
import { isHospitalDemoDepartment } from "../../../utils/hospitalDemoScore";
import type { DepartmentLocation, NurseInShift, PrintoutSchedule } from "../../../utils/models";
import { getCensus } from "../../../utils/utilFunctions";
import type { NurseInShiftWithPto, ScheduleRow, TimeSlot } from "../types";
import { applyRatioLadderToLocations, buildLocationCensusByName } from "../util/census";
import { findDepartmentLocationForNurse } from "../util/findDepartmentLocationForNurse";
import { filterLocations, filterNurseShifts, getMasterScheduleLocations, weekdayAbbreviation } from "../util/locations";
import { formatHourMinute } from "../util/shiftTimeFormat";
import { findLocationsWithNoNurse } from "./gapFinder";

interface AssembleSourceRowsArgs {
	hospitalData: PrintoutSchedule;
	weekIndex: number | undefined;
	dayIndex: number | undefined;
	department: Department;
	date: DateTime;
	staffingNurses: NurseInShift[];
	ptoShifts: NurseInShiftWithPto[];
	selectedLocations: string[];
	availableLocations: string[];
	showLocationCensus: boolean;
	timezone: string;
	dayStart: string;
	printGaps: boolean;
}

/**
 * The output of Phase 1 — everything downstream phases need to operate.
 *
 * `rows` is what the sort/group/sheets/layout phases consume. The other fields are sidecar data
 * the renderer needs (location lists for stats, census grouping for headers, dayAbbr for ladder math).
 */
export interface SourceRowsResult {
	rows: ScheduleRow[];
	departmentLocationsRequired: DepartmentLocation[];
	missingLocations: TimeSlot[];
	dayAbbr: string;
	/** Per-location-NAME rounded census for printout labels; empty when `showLocationCensus` is off. */
	censusByLocationName: Map<string, number>;
}

/**
 * Convert a gap-finder `TimeSlot` into a ScheduleRow with no nurse attached.
 */
const gapTimeSlotToScheduleRow = (slot: TimeSlot, departmentLocations: DepartmentLocation[]): ScheduleRow => {
	const deptLocation = departmentLocations.find((loc) => loc.location === slot.location);
	return {
		location: slot.location,
		section: slot.section,
		role: slot.roles[0],
		startHour: slot.start.hour,
		shiftKey: formatHourMinute(slot.start.hour, slot.start.minute),
		shiftStart: slot.start,
		shiftEnd: slot.end,
		floor: deptLocation?.floor,
	};
};

/**
 * Convert a staffed nurse shift into a ScheduleRow.
 */
const nurseShiftToScheduleRow = (shift: NurseInShift, departmentLocations: DepartmentLocation[]): ScheduleRow => {
	const deptLocation = findDepartmentLocationForNurse(shift, departmentLocations);
	return {
		location: shift.location || "",
		section: deptLocation?.section || "",
		startHour: shift.startTime.hour,
		shiftKey: formatHourMinute(shift.startTime.hour, shift.startTime.minute),
		shiftStart: shift.startTime,
		role: shift.role,
		shiftEnd: shift.endTime,
		nurse: shift,
		floor: deptLocation?.floor,
	};
};

/**
 * Phase 1 — assemble the row stream (mapping §4 phase 1).
 *
 * Steps:
 * 1. Resolve the active master-schedule locations for this date
 * 2. Apply the page-level location filter (mapping §2.2)
 * 3. Filter nurse shifts to the same location selection
 * 4. Replace each location's static `amountRequired` with the ratio-ladder-derived value
 *    (mapping §11.6 / `applyRatioLadderToLocations`)
 * 5. If `printGaps` is on, run the gap finder to identify uncovered windows
 * 6. Build ScheduleRow[]: gap rows first (so insertion order is gaps-then-staff), then staff rows
 *
 * PTO shifts are passed through to the caller separately — they don't participate in row
 * sort/group/layout, only in the PTO section at the bottom of each sheet (mapping §9.3).
 */
export const assembleSourceRows = (args: AssembleSourceRowsArgs): SourceRowsResult => {
	const {
		hospitalData,
		weekIndex,
		dayIndex,
		department,
		date,
		staffingNurses,
		selectedLocations,
		availableLocations,
		showLocationCensus,
		timezone,
		dayStart,
		printGaps,
	} = args;

	const dayObj = weekIndex !== undefined && dayIndex !== undefined ? hospitalData.weeks[weekIndex]?.weeklyShifts[0]?.days[dayIndex] : undefined;
	const dayAbbr = weekdayAbbreviation(date);
	const allActiveLocations = getMasterScheduleLocations(department, date);
	const filteredLocations = filterLocations(allActiveLocations, selectedLocations, availableLocations);
	// Census-derived required, via the canonical resolver, from the day's dimension map alone.
	// The censusByLocation grouping preference is not a parameter of this pipeline at all — it was
	// removed rather than merely unused, so the printout CANNOT disagree with the gap views because
	// one user picked a different grouping. Section headers still group by it; the numbers never do.
	const byDimensions = byDimensionsOf(dayObj?.census_grouping);
	const totalCensus = weekIndex !== undefined && dayIndex !== undefined ? getCensus(hospitalData.weeks[weekIndex], dayIndex) : 0;
	// Demo departments only. Null everywhere else, which leaves the ladder untouched. The node split is
	// built from all the department's locations, not the filtered ones, so it matches the other views.
	const demoRequired =
		weekIndex !== undefined && dayIndex !== undefined
			? demoRequiredForDay({
					department,
					week: hospitalData.weeks[weekIndex],
					dayIndex,
					isRealHospitalDemo: isHospitalDemoDepartment(department.department_id, department.type),
				})
			: null;
	const departmentLocationsRequired = applyRatioLadderToLocations(filteredLocations, byDimensions, totalCensus, dayAbbr, department.statsMode, demoRequired);
	// Reuse the same day object's dimension map for the printout's location-census labels — avoids
	// re-walking weeks/days a second time.
	const censusByLocationName = showLocationCensus
		? buildLocationCensusByName(department.locations, byDimensions, totalCensus, department.statsMode)
		: new Map<string, number>();

	const filteredNurses = filterNurseShifts(staffingNurses, selectedLocations, availableLocations);

	const missingLocations: TimeSlot[] = printGaps ? findLocationsWithNoNurse(departmentLocationsRequired, filteredNurses, date, timezone, dayStart) : [];

	const rows: ScheduleRow[] = [];
	if (printGaps) {
		for (const slot of missingLocations) rows.push(gapTimeSlotToScheduleRow(slot, departmentLocationsRequired));
	}
	for (const shift of filteredNurses) rows.push(nurseShiftToScheduleRow(shift, departmentLocationsRequired));

	return { rows, departmentLocationsRequired, missingLocations, dayAbbr, censusByLocationName };
};
